import { useMemo } from 'react';

export interface MarketPriceHistoryWindow {
  startTimeSec: number;
  endTimeSec?: number;
  fidelity: string;
}

export interface UseMarketPriceHistroyWindowOptions {
  startTime?: string | number | null;
  endTime?: string | number | null;
}

export function resolveMarketPriceHistroyWindow(
  startTime?: string | number | null,
  endTime?: string | number | null,
): MarketPriceHistoryWindow {
  const startMs = parseTimeMs(startTime);
  const nowMs = Date.now();
  const parsedEndMs = parseTimeMs(endTime);
  const endMs = Number.isFinite(parsedEndMs) ? Math.min(parsedEndMs, nowMs) : nowMs;
  const safeStartMs = Number.isFinite(startMs) ? startMs : endMs - 90 * 24 * 60 * 60 * 1000;
  const windowMinutes = Math.max(1, (endMs - safeStartMs) / (60 * 1000));

  return {
    startTimeSec: Math.floor(safeStartMs / 1000),
    endTimeSec: Number.isFinite(endMs) ? Math.floor(endMs / 1000) : undefined,
    fidelity: getMarketPriceHistoryFidelity(windowMinutes),
  };
}

function parseTimeMs(value?: string | number | null) {
  if (value === undefined || value === null || value === '') return NaN;

  const numericValue = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue > 1_000_000_000_000 ? numericValue : numericValue * 1000;
  }

  return new Date(value).getTime();
}

export default function useMarketPriceHistroyWindow({
  startTime,
  endTime,
}: UseMarketPriceHistroyWindowOptions) {
  return useMemo(() => resolveMarketPriceHistroyWindow(startTime, endTime), [endTime, startTime]);
}

function getMarketPriceHistoryFidelity(windowMinutes: number): string {
  // AlfaGPT /market/prices-history only supports 1, 60, and 1440.
  const oneDay = 24 * 60;
  const oneMonth = oneDay * 30;
  if (windowMinutes <= oneDay) return '1';
  if (windowMinutes <= oneMonth) return '60';
  return '1440';
}
